import { Autocomplete, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useMemo } from 'react'

import { i18WithParams as t } from '@shared/locale'
import { useBookingContext } from '@providers/booking'
import { TextInputNumber, TextInputPhone } from 'components-ui'
import { TState, states } from 'service-api/src/shared/states'
import { TProfileProps } from './types'

// NOTE: this form is shared between MyProfile and Details and Policies page
// if make any change here, make sure to test both pages
export const ContactInformation = ({
  formik,
  onChange,
  onChangeSelect,
  size,
  showConsentPrompt = false,
  consentToSMS,
  handleConsentToggle,
}: TProfileProps) => {
  const bookingCtx = useBookingContext()
  const theme = useTheme()

  const statesByCode: { [key: string]: TState } = useMemo(
    () =>
      states.reduce(
        (res, state) => ({
          ...res,
          [state.code]: state,
        }),
        {}
      ),
    []
  )

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
        <TextField
          id="firstName"
          label={t('contactForm.firstName')}
          variant="outlined"
          size={size}
          sx={{ flex: 1 }}
          value={formik.values.firstName}
          onChange={onChange}
          data-testid={'form-firstname'}
          error={formik.touched.firstName && Boolean(formik.errors.firstName)}
        />
        <TextField
          id="lastName"
          label={t('contactForm.lastName')}
          variant="outlined"
          size={size}
          sx={{ flex: 1 }}
          value={formik.values.lastName}
          onChange={onChange}
          data-testid={'form-lastname'}
          error={formik.touched.lastName && Boolean(formik.errors.lastName)}
        />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} mt={2} useFlexGap>
        <TextInputPhone
          id="phone"
          name="phone"
          label={t('contactForm.phone')}
          size={size}
          variant="outlined"
          value={formik.values.phone}
          sx={{ flexBasis: 0, flexGrow: 1, mr: { xs: 0, sm: 1 } }}
          onChange={onChange}
          error={formik.touched.phone && Boolean(formik.errors.phone)}
        />
        <TextField
          id="email"
          label={t('contactForm.email')}
          variant="standard"
          size={size}
          sx={{
            flex: 1,
            ml: { xs: 2, sm: 2 },
            '& .MuiInputBase-root:before': { border: 'none !important' },
          }}
          value={formik.values.email}
          onChange={onChange}
          error={formik.touched.email && Boolean(formik.errors.email)}
          disabled
        />
      </Stack>
      {showConsentPrompt && bookingCtx.state.cart.contact.smsConsentStatus === 'INACTIVE' && (
        <Stack p={1} borderRadius={1} sx={{ backgroundColor: theme.palette.action.hover }} direction={'row'}>
          <Checkbox checked={consentToSMS} onClick={handleConsentToggle} />
          <Typography variant="body2">
            {t('contactForm.smsConsent.notice', { locationName: bookingCtx.state.config.location?.displayName || '' })}
          </Typography>
        </Stack>
      )}

      <Typography variant="h6" sx={{ mt: 1, mb: 3 }}>
        {t('contactForm.mailingAddress')}
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
        <TextField
          id="streetAddress"
          label={t('contactForm.streetLine1')}
          variant="outlined"
          size={size}
          fullWidth
          value={formik.values.streetAddress}
          onChange={onChange}
          error={formik.touched.streetAddress && Boolean(formik.errors.streetAddress)}
          inputProps={{ 'data-nspace': 'mailingAddress' }}
          sx={{ flex: 2 }}
        />
        <TextField
          id="streetAddress2"
          label={t('contactForm.streetLine2')}
          variant="outlined"
          size={size}
          fullWidth
          sx={{ flex: 1 }}
          value={formik.values.streetAddress2}
          onChange={onChange}
          error={formik.touched.streetAddress2 && Boolean(formik.errors.streetAddress2)}
          inputProps={{ 'data-nspace': 'mailingAddress' }}
        />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={2} useFlexGap>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flex={2} useFlexGap>
          <TextField
            id="city"
            label={t('contactForm.city')}
            variant="outlined"
            size={size}
            sx={{ flex: 1 }}
            value={formik.values.city}
            onChange={onChange}
            error={formik.touched.city && Boolean(formik.errors.city)}
            inputProps={{ 'data-nspace': 'mailingAddress' }}
          />
          <Autocomplete
            id="state"
            sx={{ flex: 1 }}
            includeInputInList
            size={size}
            clearIcon={null}
            options={states}
            value={statesByCode?.[formik.values.state] ?? null}
            onChange={onChangeSelect}
            onBlur={formik.handleBlur}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            renderInput={(params) => (
              <TextField
                {...params}
                id="state"
                name="state"
                label={t('contactForm.state')}
                variant="outlined"
                fullWidth
                error={formik.touched.state && Boolean(formik.errors.state)}
                inputProps={{
                  ...params.inputProps,
                  'data-nspace': 'mailingAddress',
                }}
                data-nspace="mailingAddress"
              />
            )}
          />
        </Stack>
        <TextInputNumber
          id="zipCode"
          label={t('contactForm.zipCode')}
          variant="outlined"
          size={size}
          allowLeadingZeros={true}
          sx={{ flex: 1 }}
          value={formik.values.zipCode}
          onChange={onChange}
          error={formik.touched.zipCode && Boolean(formik.errors.zipCode)}
          inputProps={{ 'data-nspace': 'mailingAddress' }}
        />
      </Stack>
      <TextField
        id="country"
        name="country"
        label={t('contactForm.country')}
        variant="outlined"
        size={size}
        select
        fullWidth
        value={formik.values.country}
        onChange={onChange}
        sx={{ mt: 2 }}
        error={formik.touched.country && Boolean(formik.errors.country)}
        inputProps={{ 'data-nspace': 'mailingAddress' }}
      >
        <MenuItem value="US">{t('global.select.USA')}</MenuItem>
      </TextField>
    </Box>
  )
}
